import { useState, useEffect, useRef } from 'react';
import './App.css';
import Sidebar from './components/Sidebar';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import EVTool from './pages/EVTool';
import BetTracker from './pages/BetTracker';
import Odds from './pages/Odds';
import Props from './pages/Props';
import Pickem from './pages/Pickem';
import Arbitrage from './pages/Arbitrage';
import Calculators from './pages/Calculators';
import Results from './pages/Results';
import Projections from './pages/Projections';
import { Box, Typography, IconButton, Stack, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import logo from './assets/4f4logo.png';
import logo2 from './assets/sharpstack2.png';
import { checkUserAuth, forceRefreshIfExpired } from './auth';
import logger from './logger';
import Bugs from './pages/Bugs';
import ChangeLog from './pages/ChangeLog'; 
import Middles from './pages/Middles';
import dataStore from './dataStore';
import { useStoreData } from './useStoreData';
import { WS_URL } from './config';
// WebSocket endpoint

// Define pages that don't require authentication
const PUBLIC_PAGES = ['/odds', '/props', '/calculators', '/bugs', '/changelog', '/projections', '/results'];

// Protected Page Component
function ProtectedPage({ isAuthenticated, authError, authReason, children }) {
  // Redirect to plans page if user doesn't have betting plan
  if (!isAuthenticated && authReason === 'no-betting-plan') {
    window.location.href = 'https://www.4for4.com/plans';
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: '#121212',
          color: '#ffffff',
        }}
      >
        <Typography variant="h6">Redirecting to upgrade your plan...</Typography>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: '#121212',
          color: '#ffffff',
        }}
      >
        <Typography variant="h6">{authError || 'Please log in to access this page.'}</Typography>
      </Box>
    );
  }
  return children;
}

function App() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  const [updatesPaused, setUpdatesPaused] = useState(false);
  const [error, setError] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('Connecting');
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;
  const baseReconnectDelay = 1000;
  const isConnectingRef = useRef(false);
  const hasStoredUsername = useRef(false);

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userName, setUserName] = useState(null);
  const [authError, setAuthError] = useState(null);
  const [authReason, setAuthReason] = useState(null);
  const [userPlan, setUserPlan] = useState(null);

  // Add state and ref for dynamic header height
  const [connectionPanelHeight, setConnectionPanelHeight] = useState(48);
  const connectionPanelRef = useRef(null);

  // Check if current page requires authentication
  const isPublicPage = PUBLIC_PAGES.includes(location.pathname);
  const shouldConnectWebSocket = isAuthenticated || isPublicPage || location.pathname === '/evtool';

  // Add useEffect to measure and update the connection panel height
  useEffect(() => {
    const updateHeight = () => {
      if (connectionPanelRef.current) {
        const height = connectionPanelRef.current.getBoundingClientRect().height;
        setConnectionPanelHeight(height);
        document.documentElement.style.setProperty('--connection-panel-height', `${height}px`);
      }
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);
    const timeoutId = setTimeout(updateHeight, 100);

    return () => {
      window.removeEventListener('resize', updateHeight);
      clearTimeout(timeoutId);
    };
  }, [userName, isMobile]);

  useEffect(() => {
    const updateFilterHeight = () => {
      const filterEl = document.querySelector('.filter-row');
      if (!filterEl) return;
      const h = Math.round(filterEl.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--filter-section-height', `${h}px`);
    };
    updateFilterHeight();
    window.addEventListener('resize', updateFilterHeight);
    return () => window.removeEventListener('resize', updateFilterHeight);
  }, []);

  // ── Auto-detect user's state/country for deep link placeholders ──
  useEffect(() => {
    if (localStorage.getItem('userState')) return; // already have it
    fetch('https://ipapi.co/json/')
      .then(r => r.json())
      .then(data => {
        if (data.region_code) {
          localStorage.setItem('userState', data.region_code);
          localStorage.setItem('userCountry', data.country_code || 'US');
          logger.log(`[GEO] Detected: state=${data.region_code} country=${data.country_code}`);
        }
      })
      .catch(() => { /* silent fail — deep links fall back to defaults */ });
  }, []);

  // Track page changes
  useEffect(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && isAuthenticated && userName) {
      wsRef.current.send(JSON.stringify({
        action: 'update_page',
        page: location.pathname,
        userName: userName
      }));
      logger.log('Sent page update:', location.pathname);
    }
  }, [location.pathname, isAuthenticated, userName]);

  // NBA projections: handled by subscribe('projections') — no manual fetch needed

  const connectWebSocket = (authName, authPlan) => {
    if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) {
      logger.log('WebSocket already open or connecting, readyState:', wsRef.current.readyState);
      return;
    }
    if (isConnectingRef.current) {
      logger.log('WebSocket connection attempt already in progress');
      return;
    }

    logger.log('Initiating WebSocket connection to:', WS_URL);
    logger.log('Auth values passed to connectWebSocket - name:', authName, 'plan:', authPlan);
    isConnectingRef.current = true;
    const ws = new WebSocket(WS_URL);
    const _wsCreatedAt = performance.now();
    wsRef.current = ws;
    setConnectionStatus('Connecting');

    ws.onopen = () => {
      logger.log(`[PERF] WS connected in ${(performance.now() - _wsCreatedAt).toFixed(0)}ms`);
      const _subscribeSentAt = performance.now();
      ws._subscribeSentAt = _subscribeSentAt;
      logger.log('WebSocket connected, readyState:', ws.readyState);

      // Include authenticated flag in subscriptions
      ws.send(JSON.stringify({
        action: 'subscribe',
        type: 'arbitrage',
        authenticated: isAuthenticated,
        timestamp: Date.now()
      }));
      ws.send(JSON.stringify({
        action: 'subscribe',
        type: 'bets',
        authenticated: isAuthenticated,
        timestamp: Date.now()
      }));
      ws.send(JSON.stringify({
        action: 'subscribe',
        type: 'middles',
        authenticated: isAuthenticated,
        timestamp: Date.now()
      }));
      ws.send(JSON.stringify({
        action: 'subscribe',
        type: 'projections',
        authenticated: isAuthenticated,
        timestamp: Date.now()
      }));
      logger.log('Sent subscription messages with authenticated flag:', isAuthenticated);
      setConnectionStatus(updatesPaused ? 'Paused' : 'Connected');
      setError(null);
      reconnectAttemptsRef.current = 0;
      isConnectingRef.current = false;

      pingIntervalRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ action: 'ping' }));
          logger.log('Sent ping message');
        }
      }, 25000);

      // Use the auth values passed to this function
      const nameToUse = authName || userName;
      const planToUse = authPlan || userPlan;

      logger.log('Using auth values - name:', nameToUse, 'plan:', planToUse);
      logger.log('hasStoredUsername.current:', hasStoredUsername.current);

      // Check if we have a valid authenticated user
      if (nameToUse && nameToUse !== 'anonymous') {
        // Send auth message
        ws.send(JSON.stringify({ action: 'auth', name: nameToUse }));
        logger.log('Sent auth message with userName:', nameToUse);

        // Store username with plan
        if (!hasStoredUsername.current) {
          const storeMessage = {
            action: 'store_username',
            username: nameToUse,
            plan: planToUse
          };
          logger.log('About to send store_username message:', JSON.stringify(storeMessage));
          ws.send(JSON.stringify(storeMessage));
          logger.log('Sent store_username message:', nameToUse, 'with plan:', planToUse);
          hasStoredUsername.current = true;
        } else {
          logger.log('Skipping store_username because hasStoredUsername.current is true');
        }

        // Send initial page update
        if (location.pathname) {
          ws.send(JSON.stringify({
            action: 'update_page',
            page: location.pathname,
            userName: nameToUse
          }));
          logger.log('Sent initial page update:', location.pathname);
        }
      } else {
        // Anonymous user — still track their page
        if (location.pathname) {
          ws.send(JSON.stringify({
            action: 'update_page',
            page: location.pathname,
            userName: 'anonymous'
          }));
          logger.log('Sent anonymous page update:', location.pathname);
        }
      }
    };

    ws.onmessage = (event) => {
      try {
        const _t0 = performance.now();
        const rawSize = typeof event.data === 'string' ? event.data.length : event.data.byteLength || 0;
        const data = JSON.parse(event.data);
        const _tParse = performance.now();

        if (updatesPaused && data.type !== 'store_username_response') {
          logger.log('Updates paused, ignoring message');
          return;
        }

        if (data.type === 'store_username_response') {
          if (data.status === 'success') {
            logger.log(`Username stored successfully, userId: ${data.userId}, plan: ${data.plan}`);
          } else {
            logger.error(`Failed to store username: ${data.message}`);
          }
        } else if (data.type === 'projections_response' && data.status === 'success') {
          dataStore.setProjections(data.projections);
          logger.log('Received projections:', data.projections.length, 'records');
        // ── Bets ──
        } else if (data.type === 'bets' && data.bets) {
          const _tStore0 = performance.now();
          dataStore.setOddsSnapshot(data.bets, data.sportsbooks || null, data.markets || null, data.leagues || null, data.teams || null);
          const _tStore1 = performance.now();
          const serverMs = ws._subscribeSentAt ? (_t0 - ws._subscribeSentAt).toFixed(0) : '?';
          logger.log(`[PERF] Bets snapshot: ${data.bets.length} records, ${(rawSize / 1024).toFixed(0)} KB`);
          logger.log(`[PERF]   Server→arrive: ${serverMs}ms | JSON.parse: ${(_tParse - _t0).toFixed(0)}ms | dataStore: ${(_tStore1 - _tStore0).toFixed(0)}ms | total: ${(_tStore1 - _t0).toFixed(0)}ms`);
          ws._subscribeSentAt = null;
          setError(null);
          logger.log('Initial bets loaded:', data.bets.length, 'items');
        } else if (data.type === 'bets_delta' && data.bets) {
          dataStore.updateOdds(data.bets);
          setError(null);

        // ── Arbitrage ──
        } else if (data.type === 'arbitrage' && data.arbs) {
          const _tA0 = performance.now();
          dataStore.setArbitrage(data.arbs);
          logger.log(`[PERF] Arb snapshot: ${data.arbs.length} records, ${(rawSize / 1024).toFixed(0)} KB, parse: ${(_tParse - _t0).toFixed(0)}ms, store: ${(performance.now() - _tA0).toFixed(0)}ms`);
          setError(null);
        } else if (data.type === 'arbitrage_delta' && data.arbs) {
          dataStore.updateArbitrage(data.arbs);
          setError(null);
        } else if (data.type === 'arbitrage_remove' && data.keys) {
          dataStore.removeArbitrage(data.keys);
          setError(null);

        // ── Middles ──
        } else if (data.type === 'middles' && data.middles) {
          const _tM0 = performance.now();
          dataStore.setMiddles(data.middles);
          logger.log(`[PERF] Middle snapshot: ${data.middles.length} records, ${(rawSize / 1024).toFixed(0)} KB, parse: ${(_tParse - _t0).toFixed(0)}ms, store: ${(performance.now() - _tM0).toFixed(0)}ms`);
          setError(null);
        } else if (data.type === 'middles_delta' && data.middles) {
          dataStore.updateMiddles(data.middles);
          setError(null);
        } else if (data.type === 'middles_remove' && data.keys) {
          dataStore.removeMiddles(data.keys);
          setError(null);

        // ── Projection updates ──
        } else if (data.type === 'projections_update') {
          dataStore.updateProjections(data.projections);

        } else {
          logger.warn('Unhandled message type:', data.type);
        }
      } catch (err) {
        logger.error('Error parsing message:', err, 'Data:', event.data);
        setError('Failed to process server data. Please try refreshing.');
      }
    };

    ws.onerror = (error) => {
      logger.error('WebSocket error:', error, 'readyState:', ws.readyState);
      setConnectionStatus('Disconnected');
      setError('WebSocket connection failed. Retrying...');
      isConnectingRef.current = false;
    };

    ws.onclose = (event) => {
      logger.log('WebSocket closed, readyState:', ws.readyState, 'Code:', event.code, 'Reason:', event.reason);
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }

      hasStoredUsername.current = false;

      if (updatesPaused) {
        setConnectionStatus('Paused');
        setError(null);
        isConnectingRef.current = false;
      } else if (reconnectAttemptsRef.current < maxReconnectAttempts) {
        const delay = baseReconnectDelay * Math.pow(2, reconnectAttemptsRef.current);
        logger.log(`Reconnecting attempt ${reconnectAttemptsRef.current + 1} in ${delay}ms`);
        setConnectionStatus('Connecting');
        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectAttemptsRef.current++;
          connectWebSocket(authName, authPlan);
        }, delay);
      } else {
        setConnectionStatus('Disconnected');
        setError('Failed to connect to data server. Please refresh the page.');
        isConnectingRef.current = false;
      }
    };
  };

  // Initial setup for authentication and interval
  useEffect(() => {
    logger.log('Performing initial authentication check');

    const { isValid, name, reason, plan } = checkUserAuth();
    setIsAuthenticated(isValid);
    setUserName(name);
    setAuthReason(reason);
    setUserPlan(plan);

    if (!isValid) {
      if (reason === 'no-betting-plan') {
        setAuthError('You need a betting plan to access this feature. Redirecting to upgrade...');
      } else if (reason === 'no-params') {
        setAuthError(null);
      } else {
        setAuthError('Please log in to access the app.');
      }
    } else {
      setAuthError(null);
    }

    logger.log('Initial authentication status:', { isValid, name, reason, plan });

    const urlParams = new URLSearchParams(window.location.search);
    const timestamp = urlParams.get('timestamp');
    let interval = null;
    if (timestamp && isValid) {
      forceRefreshIfExpired(timestamp);
      interval = setInterval(() => {
        forceRefreshIfExpired(timestamp);
      }, 60 * 60 * 1000);
    }

    return () => {
      logger.log('Cleaning up WebSocket and interval');
      if (wsRef.current && (wsRef.current.readyState !== WebSocket.CLOSED && wsRef.current.readyState !== WebSocket.CLOSING)) {
        wsRef.current.close(1000, 'Normal closure');
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (interval) {
        clearInterval(interval);
      }
      isConnectingRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated && authReason === 'no-betting-plan' && !isPublicPage && location.pathname !== '/evtool') {
      logger.log('Redirecting user without betting plan to upgrade page');
      window.top.location.href = 'https://www.4for4.com/plans';
    }
  }, [isAuthenticated, authReason, isPublicPage, location.pathname]);

  useEffect(() => {
    if (shouldConnectWebSocket && userName !== null && userPlan !== null) {
      logger.log('Triggering WebSocket connection. Authenticated:', isAuthenticated, 'Public page:', isPublicPage);
      logger.log('Passing to connectWebSocket - userName:', userName, 'userPlan:', userPlan);
      connectWebSocket(userName, userPlan);
    }
  }, [shouldConnectWebSocket, userName, userPlan]);

  const handlePauseToggle = () => {
    setUpdatesPaused(prev => {
      const newState = !prev;
      setConnectionStatus(newState ? 'Paused' : wsRef.current && wsRef.current.readyState === WebSocket.OPEN ? 'Connected' : 'Connecting');
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ action: newState ? 'pause' : 'resume' }));
      }
      if (newState && reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      } else if (!newState && wsRef.current && wsRef.current.readyState !== WebSocket.OPEN) {
        connectWebSocket(userName, userPlan);
      }
      logger.log('Updates paused state changed:', newState);
      return newState;
    });
  };

 function LastUpdateDisplay() {
  const lastUpdate = useStoreData('lastUpdate');
  if (!lastUpdate) return <Typography color="#ffffff">Last Update:</Typography>;

  const dateString = lastUpdate.toLocaleString('en-US', {
    year: '2-digit', month: 'numeric', day: 'numeric',
    hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true,
  });
  const parts = dateString.split(', ');
  const timestamp = parts.length === 2 ? `${parts[0]} ${parts[1].toLowerCase()}` : dateString;

  return (
    <Typography color="#ffffff">
      Last Update:
      <Box component="span" sx={{ display: 'inline-block', ml: 1, mt: .6 }}>
        {timestamp}
      </Box>
    </Typography>
  );
}
  const getStatusTooltip = () => {
    switch (connectionStatus) {
      case 'Connected':
        return 'Connected to Database';
      case 'Connecting':
        return 'Reconnecting';
      case 'Paused':
      case 'Disconnected':
        return 'Disconnected from Database';
      default:
        return 'Unknown Status';
    }
  };

  return (
    <div className="app-container">
      <Box
        ref={connectionPanelRef}
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          backgroundColor: '#121212',
          zIndex: 1200,
          padding: '0px 12px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #444',
        }}
        className="connection-panel"
      >
        {/* Left side - Logo (always on left) */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <img
            src={logo2}
            alt="4for4 Logo"
            style={{
              width: '100px',
              height: 'auto',
              marginLeft: '0px',
            }}
          />
          {/* Desktop username (next to logo) */}
          {userName && !isMobile && (
            <Typography sx={{ color: '#ffffff', ml: 2 , mt: .6}}>
              Welcome, {userName}
            </Typography>
          )}
        </Box>

        {/* Right side - Status info and mobile username */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          ...(isMobile && { marginLeft: 'auto' })
        }}>
          {/* Mobile username (on right side) */}
          {userName && isMobile && (
            <Typography sx={{
              color: '#ffffff',
              fontSize: '0.875rem',
              whiteSpace: 'nowrap'
            }}>
              Welcome, {userName}
            </Typography>
          )}

          {/* Status information - Desktop only */}
          {!isMobile && (
            <Box sx={{
              minWidth: '250px',
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0
            }}>
              <LastUpdateDisplay />
              <Tooltip title={updatesPaused ? 'Resume Updates' : 'Pause Updates'}>
                <IconButton onClick={handlePauseToggle} sx={{ color: '#bbbbbb', ml: 1 , mt: .6}}>
                  {updatesPaused ? <PlayArrowIcon /> : <PauseIcon />}
                </IconButton>
              </Tooltip>
              <Tooltip title={getStatusTooltip()}>
                <Box
                  sx={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor:
                      connectionStatus === 'Connected' ? '#00ff00' :
                      connectionStatus === 'Paused' ? '#ff4f4f' : '#ffff00',
                    ml: 1,
                    mt: .6
                  }}
                />
              </Tooltip>
            </Box>
          )}
        </Box>
      </Box>

      <Sidebar />
      <div className="main-content" style={{
        marginTop: `${Math.min(Math.max(connectionPanelHeight || 48, 40), 120)}px`
      }}>
        {error && (
          <Box sx={{ p: 2, backgroundColor: '#ff4f4f', color: '#ffffff' }}>
            <Typography>{error}</Typography>
          </Box>
        )}
        <Routes>
          <Route path="/" element={<Navigate to="/evtool" />} />

          {/* Modified Routes - EVTool no longer wrapped in ProtectedPage */}
          <Route path="/evtool" element={
            <EVTool
              username={userName}
              isAuthenticated={isAuthenticated}
            />
          } />

          {/* Protected Routes */}
          <Route path="/bettracker" element={
            <ProtectedPage isAuthenticated={isAuthenticated} authError={authError} authReason={authReason}>
              <BetTracker username={userName} isAuthenticated={isAuthenticated} />
            </ProtectedPage>
          } />
          <Route path="/pickem" element={
            <ProtectedPage isAuthenticated={isAuthenticated} authError={authError} authReason={authReason}>
              <Pickem userName={userName} />
            </ProtectedPage>
          } />
          <Route path="/arbitrage" element={
            <ProtectedPage isAuthenticated={isAuthenticated} authError={authError} authReason={authReason}>
              <Arbitrage
                username={userName}
                isAuthenticated={isAuthenticated}
              />
            </ProtectedPage>
          } />
          <Route path="/results" element={
            <Results userName={userName} />
          } />
          <Route path="/middles" element={
            <ProtectedPage isAuthenticated={isAuthenticated} authError={authError} authReason={authReason}>
              <Middles
                username={userName}
                isAuthenticated={isAuthenticated}
              />
            </ProtectedPage>
          } />

          {/* Public Routes (no authentication required) */}
          <Route path="/odds" element={<Odds userName={userName} />} />
          <Route path="/props" element={<Props userName={userName} />} />
          <Route path="/projections" element={
                        <ProtectedPage isAuthenticated={isAuthenticated} authError={authError} authReason={authReason}>
            <Projections 
              userName={userName} 
              isAuthenticated={isAuthenticated}
            />
                        </ProtectedPage>
          } />
          <Route path="/calculators" element={<Calculators userName={userName} />} />         
          <Route path="/changelog" element={<ChangeLog userName={userName} />} />
        </Routes>
      </div>
    </div>
  );
}

export default App;